import { getIn, FormikHelpers, useFormik, setIn } from "formik";
import { useCallback, useMemo } from "react";
import { PackagesSearchParams } from "../../types/packages/PackagesSearchParams";
import { useAppAttributes } from "../useAppAttributes";
import { validationSchema } from "../../components/PackagesSearchForm/validationSchema";
import { dispatchCustomEvent } from "../../services/widgetEventsService";
import { WidgetDispatchEvents } from "../../enums/WidgetEvents";
import { CommunicationType } from "../../enums/CommunicationType";
import { DEFAULT_ROOMS } from "../../constants/sharedConstants";
import { PackagesSearchCriteria } from "../../types/packages/PackagesSearchCriteria";
import { usePackageSearchFormEvents } from "./usePackageSearchFormEvents";
import { useHotelLocation } from "../useHotelLocation";
import { useTranslation } from "../../i18n";
import { useAirportNames } from "../useAirportNames";

type OnSubmit = (
  values: PackagesSearchParams,
  formikHelpers: FormikHelpers<PackagesSearchParams>
) => void | Promise<any>;

export interface SearchFormReturnType extends ReturnType<typeof useFormik<PackagesSearchParams>> {
  hasErrors: boolean;
  setFieldsValues: (valuesChanges: Record<string, any>, shouldValidate?: boolean) => Promise<void>;
}

export const usePackagesSearchForm = (
  onSubmit: OnSubmit,
  searchCriteria?: PackagesSearchCriteria
): SearchFormReturnType => {
  const appAttributes = useAppAttributes();
  const { t } = useTranslation();
  const hotelLocation = useHotelLocation(
    searchCriteria?.destinationCode,
    searchCriteria?.destinationType
  );

  const codesForNames = useMemo(() => {
    return !!searchCriteria?.departureAirportCode ? [searchCriteria.departureAirportCode] : [];
  }, [searchCriteria?.departureAirportCode]);
  const airportNames = useAirportNames(codesForNames);

  const { setFieldValue, setFieldTouched, setValues, ...formik } = useFormik<PackagesSearchParams>({
    initialValues: {
      departureDate: searchCriteria?.departureDate
        ? new Date(searchCriteria.departureDate)
        : undefined,
      returnDate: searchCriteria?.returnDate ? new Date(searchCriteria.returnDate) : undefined,
      departureAirportCode: searchCriteria?.departureAirportCode || "",
      departureAirportName:
        !!searchCriteria?.departureAirportCode &&
        !!airportNames[searchCriteria.departureAirportCode]
          ? airportNames[searchCriteria.departureAirportCode]
          : "",
      destinationCode: searchCriteria?.destinationCode || "",
      destinationName: hotelLocation ? hotelLocation.name || t(hotelLocation.labelKey!) : "",
      destinationType: searchCriteria?.destinationType || "",
      rooms: searchCriteria?.rooms || DEFAULT_ROOMS,
    },
    onSubmit: (values, formikHelpers) => {
      if (appAttributes.communicationType === CommunicationType.Event) {
        dispatchCustomEvent({ widgetEvent: WidgetDispatchEvents.PackageOnSubmit });
      } else {
        onSubmit(values, formikHelpers);
      }
    },
    enableReinitialize: true,
    validationSchema,
  });
  const syncSetFieldValue = useCallback(
    async (field: string, value: any, shouldValidate?: boolean) => {
      await setFieldValue(field, value, shouldValidate);
      setFieldTouched(field, !!shouldValidate);
    },
    [setFieldTouched, setFieldValue]
  );

  const setFieldsValues = useCallback(
    async (valuesChanges: Record<string, any>, shouldValidate?: boolean) => {
      const entries = Object.entries(valuesChanges);
      if (entries.length === 0) return;
      if (entries.length === 1) {
        syncSetFieldValue(entries[0][0], entries[0][1], shouldValidate);
        return;
      }
      setValues((currentValues) => {
        let newValues = { ...currentValues };
        entries.forEach(([key, value]) => {
          newValues = setIn(newValues, key, value);
        });
        return newValues;
      }, shouldValidate);
    },
    [setValues, syncSetFieldValue]
  );

  const hasErrors = useMemo(() => {
    const errorsKeys = Object.keys(formik.errors);
    return errorsKeys.length > 0 && errorsKeys.some((errorKey) => getIn(formik.touched, errorKey));
  }, [formik.errors, formik.touched]);

  const baseForm = useMemo(() => {
    return {
      ...formik,
      setFieldTouched,
      setFieldValue: syncSetFieldValue,
      setValues,
      setFieldsValues,
    };
  }, [formik, setFieldTouched, syncSetFieldValue, setValues, setFieldsValues]);

  usePackageSearchFormEvents({
    form: baseForm,
  });

  return {
    ...baseForm,
    hasErrors,
  };
};
